import { useState } from 'react';
import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { Calendar, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { CampaignType } from '@/api/campaigns/types';
import type { ListCampaignsItem } from '@/api/campaigns/list-campaigns';
import { useListCampaigns } from '@/api/campaigns/list-campaigns';
import { useCopyLink } from '@/hooks/use-copy-link';
import { CampaignTypeBadge } from '@/components/shared/campaign-type-badge';
import { CampaignTypeSelector } from '@/components/shared/campaign-type-selector';
import { DialogActions } from '@/components/shared/dialog-actions';
import { ShareLinkDisplay } from '@/components/shared/share-link-display';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { storage } from '@/utils/storage';
import { formatDate } from '@/utils/date';
import { generateCampaignShareLink } from '@/utils/campaign-share-link';
import { useCreateCampaign } from '@/api/campaigns/create-campaign';
import { useOrganization } from '@/contexts/organization-context';

export const Route = createFileRoute('/_authenticated/campaigns/')({
  component: CampaignsPage
});

function CampaignsPage() {
  const navigate = useNavigate();
  const { organizationId, projectId, setProjectId } = useOrganization();
  const isDetailRoute = useRouterState({
    select: (state) => state.matches.some((match) => match.routeId === '/_authenticated/campaigns/$campaignId')
  });
  const campaignsQuery = useListCampaigns(organizationId, projectId);
  const createCampaignMutation = useCreateCampaign(organizationId, projectId);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    type: 'Form' as CampaignType,
    description: ''
  });
  const { copyLink, isLinkCopied } = useCopyLink();

  const handleCreateCampaign = () => {
    if (!projectId) {
      toast.error('Projet requis', {
        description: 'Créez un projet via l’onboarding avant de créer des campagnes.'
      });
      navigate({ to: '/onboarding' });
      return;
    }

    createCampaignMutation.mutate(
      { name: newCampaign.name.trim(), type: newCampaign.type, description: newCampaign.description.trim() },
      {
        onSuccess: (data) => {
          setIsCreateDialogOpen(false);
          setNewCampaign({ name: '', type: 'Form', description: '' });
          const shareLink = generateCampaignShareLink(data.id, data.type);
          toast.success('Campagne créée', {
            description: `Lien de partage: ${shareLink}`
          });
        },
        onError: (error) => {
          const status = (error as { status?: number }).status;
          if (status === 401 || status === 403) {
            toast.error('Accès refusé', {
              description: 'Reconnectez-vous ou vérifiez vos droits sur ce projet.'
            });
            navigate({ to: '/login', search: { redirect: '/campaigns' } });
          } else {
            toast.error('Erreur lors de la création', {
              description: error.message
            });
          }
        }
      }
    );
  };

  // Si un sous-route de détail est actif, on délègue l'affichage à ce sous-route
  if (isDetailRoute) {
    return <Outlet />;
  }

  const campaigns: Array<ListCampaignsItem> = campaignsQuery.data?.campaigns.filter((c) => !c.isInternal) ?? [];

  if (!projectId) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-20 px-4">
          <div className="container mx-auto max-w-4xl space-y-6">
            <Card className="border-2">
              <CardContent className="py-12 space-y-4 text-center">
                <h1 className="text-3xl font-bold">Créez d’abord un projet</h1>
                <p className="text-muted-foreground">
                  Pour gérer des campagnes de collecte de feedbacks, vous devez d’abord créer un projet via
                  l’onboarding.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="hero"
                    onClick={async () => {
                      storage.setHasCompletedOnboarding(false);
                      setProjectId(null);
                      await navigate({ to: '/onboarding' });
                    }}
                  >
                    Refaire l'onboarding
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-6xl space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Campagnes de{' '}
                <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">collecte</span>
              </h1>
              <p className="text-muted-foreground">Créez et suivez vos campagnes de collecte de feedbacks</p>
            </div>
            {campaigns.length >= 0 && (
              <Button onClick={() => setIsCreateDialogOpen(true)} size="lg">
                <Plus className="mr-2 h-5 w-5" />
                Nouvelle campagne
              </Button>
            )}
          </div>

          {/* Dialog de création */}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer une nouvelle campagne</DialogTitle>
                <DialogDescription>Choisissez le type de campagne que vous souhaitez créer.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="campaignName">Nom de la campagne</Label>
                  <Input
                    id="campaignName"
                    placeholder="Ex: Feedback Q1 2024"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="campaignDescription">Description</Label>
                  <Textarea
                    id="campaignDescription"
                    placeholder="Décrivez l'objectif de cette campagne..."
                    value={newCampaign.description}
                    onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <CampaignTypeSelector
                  value={newCampaign.type}
                  onValueChange={(value) => setNewCampaign({ ...newCampaign, type: value })}
                />
                <DialogActions
                  onCancel={() => setIsCreateDialogOpen(false)}
                  onConfirm={handleCreateCampaign}
                  confirmLabel={createCampaignMutation.isPending ? 'Création...' : 'Créer la campagne'}
                  disabled={createCampaignMutation.isPending}
                />
              </div>
            </DialogContent>
          </Dialog>

          {/* Liste des campagnes */}
          {campaignsQuery.isLoading ? (
            <Card className="border-2 border-dashed">
              <CardContent className="py-12 text-center text-muted-foreground">Chargement des campagnes...</CardContent>
            </Card>
          ) : campaignsQuery.isError ? (
            <Card className="border-2">
              <CardContent className="py-12 space-y-2 text-center">
                <p className="text-lg font-semibold">Impossible de charger les campagnes</p>
                <p className="text-muted-foreground">
                  {campaignsQuery.error.message || 'Vérifiez votre connexion ou vos droits.'}
                </p>
                {(campaignsQuery.error as { status?: number }).status === 401 && (
                  <Button variant="hero" onClick={() => navigate({ to: '/login', search: { redirect: '/campaigns' } })}>
                    Se reconnecter
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : campaigns.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="py-12 text-center text-muted-foreground">
                Aucune campagne créée pour le moment. Cliquez sur « Nouvelle campagne » pour en créer une.
              </CardContent>
            </Card>
          ) : (
            campaigns.length > 0 && (
              <div className="grid gap-4">
                {campaigns
                  .filter((campaign) => !campaign.isInternal)
                  .map((campaign) => {
                    const shareLink = generateCampaignShareLink(campaign.id, campaign.campaignType);
                    return (
                      <Card
                        key={campaign.id}
                        className="hover:border-primary/50 transition-all duration-300 hover:shadow-md"
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-4">
                              <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-3">
                                    <h3 className="text-xl font-semibold">{campaign.name}</h3>
                                    <CampaignTypeBadge type={campaign.campaignType} />
                                    {campaign.isInternal && <Badge variant="secondary">Internal</Badge>}
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="h-4 w-4" />
                                      {formatDate(campaign.createdOn)}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Users className="h-4 w-4" />
                                      {campaign.feedbackCount} feedback{campaign.feedbackCount > 1 ? 's' : ''}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <ShareLinkDisplay
                                link={shareLink}
                                isCopied={isLinkCopied(campaign.id)}
                                onCopy={() => copyLink(shareLink, campaign.id)}
                              />
                            </div>

                            <div className="flex items-center gap-2">
                              <Link to="/campaigns/$campaignId" params={{ campaignId: campaign.id }}>
                                <Button variant="outline" size="sm">
                                  Voir les détails
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
